import * as Sentry from '@sentry/react'
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { useToast } from '@/hooks/useToast'
import {
  deleteAccount,
  getUser,
  googleAuth,
  resendVerificationEmail,
  sendOtp,
  updateUser,
  verifyOtp,
} from '@/lib/api'
import { clearAttribution, getAttribution } from '@/lib/attribution'
import { getCurrency } from '@/lib/currencies'
import { useDisplayPrefs } from '@/lib/displayPrefs'
import { Mixpanel } from '@/lib/mixpanel'
import { getConversionUnit } from '@/lib/weight'
import { SendOtpRequest, UpdateUser, VerifyOtpRequest } from '@/types/api'

export const USER_QUERY = 'user'

export const userQueryOptions = queryOptions({
  queryKey: [USER_QUERY],
  queryFn: async () => {
    const res = await getUser()
    Mixpanel.identify(`${res.data.id}`)
    Mixpanel.people.set({
      $name: res.data.username,
      $email: res.data.email,
    })
    Sentry.setUser({ id: `${res.data.id}`, email: res.data.email, username: res.data.username })
    return res.data
  },
  retry: false,
})

export const useUserQuery = () => {
  return useQuery({
    queryKey: [USER_QUERY],
    queryFn: async () => {
      const res = await getUser()
      Mixpanel.identify(`${res.data.id}`)
      Mixpanel.people.set({
        $name: res.data.username,
        $email: res.data.email,
      })
      Sentry.setUser({ id: `${res.data.id}`, email: res.data.email, username: res.data.username })
      return res.data
    },
    select: data => ({
      ...data,
      currency: getCurrency(data.currency),
      conversion_unit: getConversionUnit(data.unit_weight),
    }),
    retry: false,
  })
}

export const useSendOtp = () => {
  return useMutation({
    mutationFn: async (params: SendOtpRequest) => {
      const res = await sendOtp(params)
      return res.data
    },
  })
}

export const useVerifyOtp = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (params: VerifyOtpRequest) => {
      // Ad attribution rides along on registrations; the API keeps it only
      // when this call creates the account.
      const attribution = params.is_registration ? getAttribution() : undefined
      const res = await verifyOtp({ ...params, attribution })
      queryClient.setQueryData([USER_QUERY], res.data.user)
      clearAttribution()
      return { ...res.data, attribution }
    },
  })
}

export const useGoogleAuth = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (credential: string) => {
      // Google sign-in creates the account when there isn't one, from the
      // login page too, so attribution is always sent.
      const attribution = getAttribution()
      const res = await googleAuth(credential, attribution)
      queryClient.setQueryData([USER_QUERY], res.data.user)
      clearAttribution()
      return { ...res.data, attribution }
    },
  })
}

export const useResendVerification = () => {
  const { toast } = useToast()
  return useMutation({
    mutationFn: async () => {
      const res = await resendVerificationEmail()
      Mixpanel.track('User:ResendVerification')
      return res.data
    },
    onSuccess: () => {
      toast({
        title: 'Verification email sent',
        description: 'Check your inbox for the verification link.',
      })
    },
  })
}

/**
 * Deletes the account and tears down local identity the same way logout
 * does. The caller navigates: the query cache is cleared here, so the
 * router's user guard would otherwise refetch and 401.
 */
export const useDeleteAccount = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await deleteAccount()
      Mixpanel.track('User:AccountDeleted')
      Mixpanel.reset()
      Sentry.setUser(null)
      queryClient.clear()
    },
  })
}

export const useUpdateUser = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: async (params: UpdateUser) => {
      const res = await updateUser(params)
      return res.data
    },
    onSuccess: (_, params) => {
      queryClient.invalidateQueries({ queryKey: [USER_QUERY] })
      // A saved weight unit wins over the session's Options-menu override.
      if (params.unit_weight) useDisplayPrefs.getState().setWeightSystem(null)
      toast({
        title: '✅ Settings updated',
      })
    },
  })
}
